import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppstoreOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Form,
  Image,
  Input,
  Popover,
  Radio,
  Select,
  Slider,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { Field, FieldArray, useFormikContext } from "formik";
import { t } from "i18next";
import { PremiumSvg } from "@/Assets/Index";
import { navigate } from "@/Components/Common/NavigationMenu";
import { ProfileContext } from "@/Context/ProfileContext";
import { getValueFromNestedObject, isObject } from "@/Utils/Index";
import Codemirror from "./CodeMirror";
import ColorPickerCircle from "./ColorPicker";
import CustomSelector from "./CustomSelector";
import EditorComponent from "./EditorComponent";
import FileUpload from "./FileUpload";
import Fontpicker from "./FontPicker";
import HelpTextSelector from "./HelpTextSelector";
import ImagePicker from "./ImagePicker";
import LoctionPostion from "./LoctionPostion";
import MultiSelect from "./MultiSelect";
import { PremiumButton } from "./PremiumBadge";
import SearchAutocomplete from "./SearchAutocomplete";
import SearchableSelect from "./SearchableSelect";
import Switch from "./Switch";
import TinyEditorComponent from "./TinymceEditor/TinyEditorComponent";
import VideoRecordInput from "./VideoRecorder";
import VoiceRecorder from "./VoiceRecorder";

const { Text } = Typography;

const timeRegex = /^(([0-1]?[0-9]|2[0-3]):)?([0-5]?[0-9]:)?([0-5]?[0-9])?$/;

const Delete = (props) => {
  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(() => setPopoverActive((popoverActive) => !popoverActive), []);

  const yes = (e) => {
    props.remove(props.index);
    setPopoverActive(false);
  };
  const no = (e) => setPopoverActive(false);

  if (props.minimum > props.index) {
    return <React.Fragment></React.Fragment>;
  }

  return (
    <Popover
      open={popoverActive}
      onOpenChange={setPopoverActive}
      content={
        <div style={{ padding: "8px" }}>
          <Space direction="vertical" size="small">
            <div>Are you sure you want to delete?</div>
            <Space>
              <Button size="small" onClick={yes}>
                Yes
              </Button>
              <Button size="small" onClick={no}>
                No
              </Button>
            </Space>
          </Space>
        </div>
      }
      trigger="click"
    >
      <div style={{ textAlign: "right" }}>
        <Button type="text" icon={<DeleteOutlined />} size="small" onClick={togglePopoverActive} />
      </div>
    </Popover>
  );
};

const Add = (props) => {
  const { t } = useTranslation();
  return props.type !== "single" ? (
    <Button
      type="dashed"
      icon={<PlusOutlined />}
      onClick={() =>
        props.push(
          Object.fromEntries(
            props.subfields.map((subfield) => {
              if (subfield.type == "array") {
                return [subfield.name, subfield.initialValue ?? []];
              } else {
                return [subfield.name, ""];
              }
            })
          )
        )
      }
    >
      {t("common.FormComponent.Add ")} {props.label}
    </Button>
  ) : (
    <Button type="dashed" icon={<PlusOutlined />} onClick={() => props.push("")}>
      {t("common.FormComponent.Add ")} {props.label}
    </Button>
  );
};

export const prefixFn = (prefix) => {
  const { profileData } = useContext(ProfileContext);
  const [prefixVal, setPrefixVal] = useState();

  useEffect(() => {
    if (profileData) {
      setPrefixVal(eval(prefix));
    }
  }, [profileData]);

  return prefixVal;
};

export const PremiumIcon = (props) => {
  const { t } = useTranslation();

  const { label, premiumType, planName } = props;
  const setNavigate = navigate();
  const { profileData } = useContext(ProfileContext);
  const [popoverActive, setPopoverActive] = useState(false);

  const gotoPrice = () => {
    setNavigate("/pricing");
  };

  const togglePopoverActive = useCallback(() => setPopoverActive((popoverActive) => !popoverActive), []);

  const activator = (
    <Button type="text" onClick={togglePopoverActive}>
      <Badge color="blue">
        <Space align="center" size="small">
          <Image src={PremiumSvg} width={15} preview={false} />
          <Text style={{ fontSize: "12px" }}>{planName || "Premium"}</Text>
        </Space>
      </Badge>
    </Button>
  );

  const Icon = (
    <div className="premium_icon">
      <Space size="small">
        <div>{label}</div>
        <Popover
          open={popoverActive}
          onOpenChange={setPopoverActive}
          content={
            <div style={{ padding: "8px" }}>
              <Space direction="vertical" size="small">
                <Text strong>{t(`common.Premium.Included in the ${planName || "Premium"} plan`)}</Text>
                <Button type="text" onClick={gotoPrice}>
                  {t("common.FormComponent.Go to pricing")}
                </Button>
              </Space>
            </div>
          }
          placement="top"
          trigger="click"
        >
          {activator}
        </Popover>
      </Space>
    </div>
  );

  // Improved logic for showing the icon or label
  if (premiumType && profileData?.featureData) {
    if (profileData?.featureData?.[premiumType] === "false" || !profileData?.featureData?.[premiumType])
      return Icon;
    return <div>{label}</div>;
  } else if (profileData?.recurringPlanType === "Free") return Icon;
  else return <div>{label}</div>;
};

const DisableFields = (value, premiumType) => {
  const { profileData } = useContext(ProfileContext);
  const [disable, setDisable] = useState(value);

  useEffect(() => {
    if (profileData?.recurringPlanType !== "Free") {
      if (
        premiumType &&
        profileData?.featureData &&
        (profileData?.featureData?.[premiumType] === "false" || !profileData.featureData[premiumType])
      ) {
        setDisable(true);
      } else setDisable(false);
    } else {
      setDisable(true);
    }
  }, [profileData]);

  return disable;
};

const FormField = (props) => {
  const {
    field: {
      helpTextDynamic,
      helpText,
      helpTextFn,
      requiredIndicator,
      validated,
      pattern,
      type,
      onChange,
      name,
      key,
      showPremium,
      label,
      prefix,
      prefixDynamic,
      premiumType,
      disabled,
      planName,
    },
    form: { handleChange, handleBlur },
    error,
    value,
    touch,
    field,
  } = props;
  const fieldProps = {
    ...props.field,
    ...props,
    name,
    label: showPremium ? <PremiumIcon label={label} premiumType={premiumType} planName={planName} /> : label,
    disabled: showPremium ? DisableFields(disabled, premiumType) : disabled,
    value: value,
    helpText: helpTextDynamic ? helpTextFn(props) : helpText,
    prefix: prefixDynamic ? prefixFn(prefix) : prefix,
    requiredIndicator: JSON.stringify(requiredIndicator) ? requiredIndicator : validated,
    validate: props.field.customValidate,
    onChange: onChange
      ? (value) => {
          onChange(value, props.form);
        }
      : (value) => {
          if (!pattern || timeRegex.test(value) || value === "") {
            handleChange({ target: { name, value } });
          }
        },
    onBlur: () => handleBlur({ target: { name } }),
    error: error && touch && error,
  };

  if (type === "SearchAutocomplete") {
    return (
      <React.Fragment key={key}>
        <Field
          {...fieldProps}
          component={SearchAutocomplete}
          validate={(value) => {
            if (!value) {
              return `${label} is required`;
            }
          }}
        />
      </React.Fragment>
    );
  }

  if (type === "colorPicker") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={ColorPickerCircle} />
      </React.Fragment>
    );
  }

  if (type === "editor") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={EditorComponent} />
      </React.Fragment>
    );
  }

  if (type === "tiny-editor") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={TinyEditorComponent} />
      </React.Fragment>
    );
  }

  if (type === "multiSelect") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={MultiSelect} />
      </React.Fragment>
    );
  }
  if (type === "component") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={field.component} />
      </React.Fragment>
    );
  }

  if (type === "searchableSelect") {
    return (
      <React.Fragment key={key}>
        <Field
          {...fieldProps}
          validate={(value) => {
            return searchableSelectValidation(value, props.field.options);
          }}
          component={SearchableSelect}
        />
      </React.Fragment>
    );
  }

  if (type === "rangeSlider") {
    const { showOutput = true, defaultSuffix = true, min = 0, max = 100, step = 1 } = fieldProps;
    return (
      <div>
        <Text style={{ marginBottom: "8px", display: "block" }}>{fieldProps.label}</Text>
        <Slider
          {...fieldProps}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={fieldProps.onChange}
          tooltip={{ formatter: showOutput ? (val) => `${fieldProps.suffix || ""}${val}` : null }}
        />
        {defaultSuffix && showOutput && (
          <Text style={{ fontSize: "12px", color: "#666", textAlign: "right", display: "block" }}>
            {fieldProps.suffix || ""}
            {value}
          </Text>
        )}
      </div>
    );
  }

  if (type === "img") {
    const sizeMap = { small: 32, medium: 48, large: 64 };
    const imageSize = sizeMap[props.field.size] || 48;
    return (
      <Image
        src={props.value || AppstoreOutlined}
        width={imageSize}
        height={imageSize}
        alt={props.name}
        style={{ objectFit: "cover" }}
      />
    );
  }

  if (type === "switch") {
    return <Switch {...fieldProps} checked={props.value} />;
  }

  if (type === "tiny-editor") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={TinyEditorComponent} />
      </React.Fragment>
    );
  }

  if (type === "checkbox") {
    return (
      <Checkbox {...fieldProps} checked={props.value}>
        {fieldProps.label}
      </Checkbox>
    );
  }
  if (type === "array") {
    field.groupSize = field.groupSize ? field.groupSize : 2;
    return (
      <FieldArray key={field.id} name={name}>
        {({ push, remove }) => (
          <React.Fragment key={field.id}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Text>{field.label}</Text>
              <div>
                {[...Array(Math.ceil(value?.length / field?.groupSize))].map((_, gIndex) => (
                  <Space key={gIndex} wrap style={{ width: "100%", marginBottom: "8px" }}>
                    {value
                      ?.slice(gIndex * field.groupSize, gIndex * field.groupSize + field.groupSize)
                      .map((value, index) => {
                        const finalIndex = gIndex * field.groupSize + index;
                        return (
                          <div key={finalIndex} style={{ position: "relative" }}>
                            <Field
                              name={`${name}.[${finalIndex}]`}
                              field={{
                                ...props.field,
                                type: props.field.arrayType,
                                name: `${name}.[${finalIndex}]`,
                              }}
                              component={FormField}
                              value={value}
                              error={error?.[finalIndex] ? error?.[finalIndex] : false}
                              touch={touch?.[finalIndex] ? touch?.[finalIndex] : false}
                            />
                            {!field.hideDeletebtn && props.field.minimum <= finalIndex && (
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() => remove(finalIndex)}
                                style={{ position: "absolute", top: 0, right: -24 }}
                              />
                            )}
                          </div>
                        );
                      })}
                  </Space>
                ))}
              </div>
              {!field.hideAddbtn && <Add {...field} push={push} type="single" />}
            </Space>
          </React.Fragment>
        )}
      </FieldArray>
    );
  }

  if (type === "radio") {
    return (
      <Radio
        {...fieldProps}
        value={props.field.radioId}
        checked={value === props.field.radioId}
        onChange={(e) => {
          const radioValue = e.target.value;
          props.onChange && props.onChange(radioValue, props.form);
          if (!pattern || timeRegex.test(radioValue) || radioValue === "") {
            handleChange({ target: { name, value: radioValue } });
          }
        }}
      >
        {fieldProps.label}
      </Radio>
    );
  }

  if (type === "select") {
    return (
      <div>
        {fieldProps.label && <Text style={{ marginBottom: "8px", display: "block" }}>{fieldProps.label}</Text>}
        <Select {...fieldProps} style={{ width: "100%" }} value={value} onChange={fieldProps.onChange}>
          {fieldProps.options?.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  }
  if (type === "helpTextSelector") {
    return (
      <React.Fragment key={key}>
        <Field
          {...fieldProps}
          component={HelpTextSelector}
          validate={(value) => {
            if (props.field.validated) {
              if (value.replace(/(<([^>]+)>)/gi, "").trim()) {
                return false;
              } else {
                return props.field.errormsg;
              }
            } else {
              return false;
            }
          }}
        />
      </React.Fragment>
    );
  }

  if (type === "imagePicker") {
    return (
      <React.Fragment key={key}>
        <Field
          {...fieldProps}
          validate={(value) => {
            if (!value) {
              value = [];
            }
            if (isObject(value)) {
              value = [value];
            }
            if (props.field.min && props.field.max) {
              if (props.field.min > value.length || value.length > props.field.max) {
                return `Minimum ${props.field.min} and Maximum ${props.field.max} ${props.field.label}s are required`;
              }
            } else if (props.field.min) {
              if (props.field.min > value.length) {
                return `Minimum ${props.field.min} ${props.field.label}s are required`;
              }
            } else if (props.field.max) {
              if (value.length > props.field.max) {
                return `Maximum ${props.field.max} ${props.field.label}s are allowed`;
              }
            }
            return false;
          }}
          component={ImagePicker}
        />
      </React.Fragment>
    );
  }

  if (type === "fileUpload") {
    return (
      <React.Fragment key={key}>
        <Field
          {...fieldProps}
          validate={(value) => {
            if (!value) {
              value = [];
            }
            if (isObject(value)) {
              value = [value];
            }
            if (props.field.min && props.field.max) {
              if (props.field.min > value.length || value.length > props.field.max) {
                return `Minimum ${props.field.min} and Maximum ${props.field.max} ${props.field.label}s are required`;
              }
            } else if (props.field.min) {
              if (props.field.min > value.length) {
                return `Minimum ${props.field.min} ${props.field.label}s are required`;
              }
            } else if (props.field.max) {
              if (value.length > props.field.max) {
                return `Maximum ${props.field.max} ${props.field.label}s are allowed`;
              }
            }
            return false;
          }}
          component={FileUpload}
        />
      </React.Fragment>
    );
  }

  if (type === "codeMirror") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={Codemirror} />
      </React.Fragment>
    );
  }

  if (type === "fontpicker") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={Fontpicker} />
      </React.Fragment>
    );
  }

  if (type === "locationPosition") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={LoctionPostion} />
      </React.Fragment>
    );
  }
  if (type === "choiceList") {
    return (
      <div>
        {fieldProps.label && <Text style={{ marginBottom: "8px", display: "block" }}>{fieldProps.label}</Text>}
        <Radio.Group {...fieldProps} value={props.value} onChange={(e) => fieldProps.onChange(e.target.value)}>
          {fieldProps.choices?.map((choice) => (
            <Radio key={choice.value} value={choice.value}>
              {choice.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>
    );
  }

  if (type === "audio") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={VoiceRecorder} />
      </React.Fragment>
    );
  }

  if (type === "video") {
    return (
      <React.Fragment key={key}>
        <Field
          {...fieldProps}
          component={VideoRecordInput}
          validate={(value) => {
            if (props.field.validated) {
              if (value) {
                return false;
              } else {
                return props.field.errormsg;
              }
            } else {
              return false;
            }
          }}
        />
      </React.Fragment>
    );
  }
  if (type === "customSelector") {
    return (
      <React.Fragment key={key}>
        <Field {...fieldProps} component={CustomSelector} />
      </React.Fragment>
    );
  }

  // Default text field
  return (
    <div>
      {fieldProps.label && <Text style={{ marginBottom: "8px", display: "block" }}>{fieldProps.label}</Text>}
      <Input
        {...fieldProps}
        value={value}
        onChange={(e) => fieldProps.onChange(e.target.value)}
        status={fieldProps.error ? "error" : ""}
        placeholder={fieldProps.placeholder}
      />
      {fieldProps.error && (
        <Text type="danger" style={{ fontSize: "12px", marginTop: "4px", display: "block" }}>
          {fieldProps.error}
        </Text>
      )}
      {fieldProps.helpText && (
        <Text type="secondary" style={{ fontSize: "12px", marginTop: "4px", display: "block" }}>
          {fieldProps.helpText}
        </Text>
      )}
    </div>
  );
};

const Error = ({ name }) => {
  const { errors, touched } = useFormikContext();
  const error = errors[name];
  const touch = touched[name];
  return touch && error ? (
    <Alert message={error} type="error" showIcon style={{ marginTop: "4px", fontSize: "12px" }} />
  ) : null;
};

const SaveButton = ({
  isSave = true,
  isPremium = false,
  label = t("common.FormComponent.Save"),
  premiumFeatureName,
  buttonVariant = "primary",
}) => {
  if (isSave) {
    if (isPremium) {
      return (
        <PremiumButton type={premiumFeatureName}>
          <Button type="primary" htmlType="submit">
            {label}
          </Button>
        </PremiumButton>
      );
    } else {
      return (
        <Button type="primary" htmlType="submit">
          {label}
        </Button>
      );
    }
  }
};

const searchableSelectValidation = (value, options) => {
  if (options.findIndex((option) => option.value === value) === -1) {
    return "Select valid option.";
  }
  return false;
};

const DynamicSection = ({ children, field }) => {
  if (field.section !== undefined && !field.section) {
    return <React.Fragment>{children}</React.Fragment>;
  } else {
    return <Card style={{ padding: "16px" }}>{children}</Card>;
  }
};

export { Delete, Add, FormField, Error, SaveButton, DynamicSection };
